<template>
  <div class="row">
    <model-index
      ref="table"
      class="col-12"
      :loading="loading"
      v-bind="$props"
      :columns="columns"
      :busy.sync="busy"
      :models-paginated="modelsPaginated"
      :models="models"
      :page.sync="page"
      :per-page.sync="perPage"
      :from="from"
      :total="totalFiltered"
      :search.sync="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :empty-state-title="lodash.startCase($t(purchaseOrdersLoadError ? 'purchase_orders.load_failed' : 'purchase_orders.empty_state.title'))"
      :empty-state-subtitle="purchaseOrdersLoadError ? $t('purchase_orders.load_failed_description') : ' '"
      @create-click="handleCreate"
      @show-click="handleShow"
      @edit-click="handleEdit"
      @delete-click="handleDelete"
    >
      <template #card-tools>
        <span v-if="eligibilityError && models.length > 0" class="d-inline-flex align-items-center me-2">
          <span class="text-warning me-2">{{ lodash.startCase($t('purchase_orders.eligibility_check_failed')) }}</span>
          <b-button variant="outline-primary" size="sm" :disabled="eligibilityLoading" @click="retryEligibilityCheck">
            {{ $t('Retry') }}
          </b-button>
        </span>
        <b-button v-if="canCreate && models.length > 0" variant="primary" size="sm" @click="handleCreate">
          {{ $t('purchase_orders.empty_state.action') }}
        </b-button>
      </template>
      <template #emptyStateAction>
        <div v-if="purchaseOrdersLoadError" class="d-flex justify-content-center">
          <b-button variant="primary" :disabled="busy" @click="retryLoad">
            {{ $t(busy ? 'fetch_data' : 'Retry') }}
          </b-button>
        </div>
        <div v-else-if="eligibilityError" class="d-flex flex-column align-items-center justify-content-center">
          <span class="text-warning mb-2">{{ lodash.startCase($t('purchase_orders.eligibility_check_failed')) }}</span>
          <b-button variant="outline-primary" :disabled="eligibilityLoading" @click="retryEligibilityCheck">
            {{ $t('Retry') }}
          </b-button>
        </div>
        <div v-else-if="canCreate" class="d-flex justify-content-center">
          <b-button variant="primary" @click="handleCreate">
            {{ $t('purchase_orders.empty_state.action') }}
          </b-button>
        </div>
      </template>
    </model-index>
  </div>
</template>

<script>
import ModelChildIndex from "../../models/children/index";
import icons from "@/assets/icons";
import JalaAppIcon from "@/assets/icons/JalaApp.svg";
import JalaHarvestIcon from "@/assets/icons/JalaHarvest.svg";
import JalaProIcon from "@/assets/icons/JalaPro.svg";
import {hasAllowedAction, normalizeAllowedActions} from "@/helpers/ui/allowedActions";

export default {
  extends: ModelChildIndex,
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("purchase_orders.plural"));
      }
    },
    resourceRoute: {default: "purchase_orders"},
    modelVariable: {default: "purchase_order"},
    tableMinHeight: {type: Number, default: 85.5}, // 42.5 + 43
    tableHeight: {type: Number, default: 214.5}, // 42.5 + (43 * 4)
    denyCreate: {type: Boolean, default: true},
    createRoute: {type: [String, Object], default: null},
    hideTableHeader: {type: Boolean, default: true},
    values: {
      type: Array,
      default() {
        return [];
      }
    },
    showProps: {
      default() {
        return {
          cardTitle: this.lodash.startCase(this.$t("purchase_orders.singular")),
          resourceRoute: "purchase_orders",
          modelVariable: "purchase_order",
          modelMapped: require("@/router/views/purchase_orders/show.vue").default.computed.modelMapped.bind(this),
          denyEdit: true,
          denyDelete: true
        };
      }
    }
  },
  data() {
    return {
      sortBy: "ordered_at",
      sortDesc: true,
      allowedActions: [],
      hasEligiblePurchasePlan: false,
      eligibilityError: false,
      eligibilityLoading: false,
      purchaseOrdersLoadError: false
    };
  },
  computed: {
    canCreate() {
      return this.hasEligiblePurchasePlan && hasAllowedAction(this.allowedActions, ["create", "store"]);
    },
    columns() {
      return {
        ["purchase_order"]: [
          {
            key: "id",
            label: this.lodash.startCase(this.$t("tickets.purchase_order_id")),
            thStyle: {minWidth: "125px"},
            component: {
              props: ["item"],
              template: '<router-link :to="`/purchase_orders/${item.id}`">{{ `${item.id }` }}</router-link>'
            }
          },
          {
            key: "ordered_at",
            label: this.lodash.startCase(this.$t("purchase_orders.ordered_at")),
            thStyle: {minWidth: "120px"},
            component: {
              props: ["item"],
              computed: {
                date() {
                  return this.$moment
                      .utc(this.item.ordered_at || this.item.estimated_harvest_date)
                      .local()
                      .format("DD MMM, YYYY");
                }
              },
              template: `
                <div>
                  {{ date }}
                </div>
              `
            }
          },
          {
            key: "customer",
            label: this.lodash.startCase(this.$t("purchase_orders.partner")),
            thStyle: {width: "70px"},
            component: {
              props: ["item"],
              template:
                  '<div style="width: 100px" class="" :title="(item.customer || {}).name">{{ (item.customer || {}).name }}</div>'
            }
          },
          {
            key: "farmer.name",
            label: this.lodash.startCase(this.$t("purchase_orders.farmer")),
            thStyle: {minWidth: "150px"},
            tdClass: "separator-badge",
            component: {
              props: ["item"],
              data() {
                return {
                  icons,
                  JalaAppIcon,
                  JalaHarvestIcon,
                  JalaProIcon
                };
              },
              computed: {
                icon() {
                  if (!this.item.farmer) {
                    return false;
                  }
                  if (this.item.pro_cycle) {
                    return this.icons.JalaPro;
                  }
                  if (this.item.farm.use_jala_farm) {
                    return this.icons.JalaApp;
                  } else {
                    return this.icons.JalaHarvest;
                  }
                },
                tooltip() {
                  if (this.item.pro_cycle) {
                    return "Terdaftar dalam Siklus Pro";
                  } else if (this.item.farm?.use_jala_farm) {
                    return "Pengguna JALA Apps";
                  }
                  return "Mitra JALA Harvest";
                }
              },
              template: `
                <div v-if="item.farmer" class="d-flex align-items-center gap-2" v-b-tooltip.hover :title="tooltip">
                  <div class="d-flex align -items-center">
                    <img style="width: 16px; height: 16px" v-if="item.pro_cycle" :src="JalaProIcon"></img>
                    <img style="width: 16px; height: 16px" v-else-if="item.farm && item.farm.use_jala_farm"
                         :src="JalaAppIcon"></img>
                    <img style="width: 16px; height: 16px" v-else :src="JalaHarvestIcon"></img>
                  </div>
                  <div>
                    {{ lodash.get(item, "farmer.name") }}
                  </div>
                </div>
                <span v-else>-</span>
              `
            }
          },
          {
            key: "purchase_item_total_quantity",
            label: this.lodash.startCase(this.$t("purchase_orders.total_biomassa")),
            thStyle: {minWidth: "125px"},
            thClass: "text-end",
            tdClass: "text-end",
            formatter: value => `${this.$numeral(value / 1000).format("0,0.[000]")} ${this.$t("units.ton")}`
          },
          {
            key: "total",
            label: this.lodash.startCase(this.$t("purchase_orders.nominal")),
            thStyle: {minWidth: "125px"},
            thClass: "text-end",
            tdClass: "text-end",
            formatter: value => {
              return this.$numeral(value).format("$ 0,0.[00]");
            }
          },
          {
            key: 'odoo_purchase_order.state',
            label: this.lodash.startCase(this.$t("purchase_orders.odoo_purchase_orders.state")),
            component: {
              props: ["item"],
              computed: {
                translatedState() {
                  let state = this.item.odoo_purchase_order?.state
                  if (!state) {
                    return '-'
                  }
                  return this.$t(`purchase_orders.odoo_purchase_orders.states.${state}`)
                },
              },
              template:`
                <status-badge
                    v-if="translatedState != '-'"
                    entity="odoo_purchase_order"
                    type="status"
                    :status="item.odoo_purchase_order.state"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
          {
            key: 'odoo_purchase_order.invoice_status',
            label: this.lodash.startCase(this.$t("purchase_orders.odoo_purchase_orders.invoice_status")),
            component: {
              props: ["item"],
              computed: {
                translatedInvoiceStatus() {
                  let invoiceStatus = this.item.odoo_purchase_order?.invoice_status
                  if (!invoiceStatus) {
                    return '-'
                  }
                  return this.$t(`purchase_orders.odoo_purchase_orders.invoice_statuses.${invoiceStatus}`)
                },
              },
              template:`
                <status-badge
                    v-if="translatedInvoiceStatus != '-'"
                    entity="odoo_purchase_order"
                    type="invoice_status"
                    :status="item.odoo_purchase_order.invoice_status"
                ></status-badge>
                <span v-else>-</span>
              `
            },
          },
        ]
      };
    }
  },
  methods: {
    fetchEligiblePurchasePlan() {
      this.eligibilityLoading = true;
      this.eligibilityError = false;
      return this.$axios.get('/v1/purchase_plans', {
        params: {
          sales_plan_id: this.parent.id || this.$route.params.key,
          status: 'approved',
          has_purchase_order: '0',
          per_page: 1,
        }
      }).then(({data: {data}}) => {
        this.hasEligiblePurchasePlan = data.length > 0;
        return this.hasEligiblePurchasePlan;
      })
        .catch(() => {
          this.eligibilityError = true;
          return null;
        })
        .finally(() => {
          this.eligibilityLoading = false;
        });
    },
    retryEligibilityCheck() {
      return this.fetchEligiblePurchasePlan();
    },
    retryLoad() {
      if (this.purchaseOrdersLoadError) {
        this.busy = true;
        return this.fetchModels(this.page || 1).finally(() => {
          this.busy = false;
        });
      }
      return this.retryEligibilityCheck();
    },
    handleCreate() {
      if (!this.canCreate) {
        return;
      }

      if (this.createRoute) {
        return this.$router.push(this.createRoute);
      }

      return ModelChildIndex.methods.handleCreate.call(this);
    },
    store(value) {
      return this.$axios.post(`/purchase_orders`, value);
    },
    update(value) {
      return this.$axios.put(`/purchase_orders/${this.getModelKey(value)}`, value);
    },
    delete(value) {
      return this.$axios.delete(`/purchase_orders/${this.getModelKey(value)}`);
    },
    fetchModels(page = 1) {
      this.allowedActions = [];
      this.hasEligiblePurchasePlan = false;
      this.eligibilityError = false;
      this.purchaseOrdersLoadError = false;

      const purchaseOrdersRequest = this.$axios
          .get(`/purchase_orders`, {
            params: {
              page: page,
              per_page: this.perPage,
              search: this.search,
              sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
              with: [
                "creator",
                "customer",
                "odoo_purchase_order.odoo_purchase_order_lines",
                "paymentables",
                "ticket",
                "shrimp",
                "farmer",
                "farm"
              ].join(","),
              sales_plan_id: this.parent.id || this.$route.params.key,
              ...this.filterParams
            }
          });

      return Promise.all([purchaseOrdersRequest, this.fetchEligiblePurchasePlan()])
          .then(([response, hasEligiblePurchasePlan]) => {
            const {
              data: {data, meta}
            } = response;
            this.allowedActions = normalizeAllowedActions(meta?.allowed_actions);
            this.hasEligiblePurchasePlan = hasEligiblePurchasePlan;
            this.models = data.map(datum => ({
              ...datum,
              product: {
                ...datum.product,
                name: (datum.price && datum.product.name) || this.lodash.startCase(this.$t("tickets.allowance"))
              },
              _denyShow: false,
              _denyEdit: !hasAllowedAction(datum, ["edit", "update"]),
              _denyDelete: !hasAllowedAction(datum, ["delete", "destroy"])
            }));
            this.page = Number(meta.current_page);
            this.perPage = Number(meta.per_page);
            this.from = Number(meta.from);
            this.total = Number(meta.total);

            this.stats = [];

            return response;
          })
          .catch(() => {
            this.allowedActions = [];
            this.models = [];
            this.purchaseOrdersLoadError = true;
            return null;
          });
    }
  }
};
</script>
